import { useMutation } from '@apollo/client';
import type { DeleteDataImportByIdMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteDataImportByIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteDataImportByIdTRPC } from './useDeleteDataImportByIdTRPC';

/**
 * Flag-gated wrapper for the `deleteDataImportById` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Both paths return the same
 * `DeleteDataImportByIdMutation` shape.
 */
export const useDeleteDataImportById = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [deleteGraphQL, graphqlState] = useMutation(
    DeleteDataImportByIdDocument,
    {
      update: (cache) => {
        evictField(cache, 'data_import');
      },
    }
  );

  const trpcMutation = useDeleteDataImportByIdTRPC();

  const deleteDataImportById = async (
    id: string
  ): Promise<DeleteDataImportByIdMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteDataImportById(id);
    }

    const result = await deleteGraphQL({ variables: { id } });
    if (!result.data) {
      throw new Error('Failed to delete data import');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteDataImportById,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteDataImportById,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
